import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

/* ---------- Types ---------- */

interface StudyOption {
  studyid: string;
  studyname: string;
}

interface StudyContextType {
  /** Currently selected global study (null = no study selected) */
  selectedStudy: StudyOption | null;
  /** Call this when the user picks / clears a study from the Header dropdown */
  setGlobalStudy: (study: StudyOption | null) => void;
  isStudyLoading: boolean;
  setIsStudyLoading: (loading: boolean) => void;
}

/* ---------- Context ---------- */

const StudyContext = createContext<StudyContextType | undefined>(undefined);

/* ---------- Provider ---------- */

export const StudyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isStudyLoading, setIsStudyLoading] = useState<boolean>(false);

  // Hydrate initial state from localStorage so a page-refresh keeps the selection
  const [selectedStudy, setSelectedStudy] = useState<StudyOption | null>(() => {
    try {
      const id = sessionStorage.getItem("globalstudyid");
      const name = sessionStorage.getItem("globalstudyname");
      if (id) return { studyid: id, studyname: name || "" };
    } catch {
      /* empty */
    }
    return null;
  });

  const setGlobalStudy = useCallback((study: StudyOption | null) => {
    setIsStudyLoading(true);
    setSelectedStudy(study);
    // Keep localStorage in sync for persistence across hard-refreshes
    if (study) {
      sessionStorage.setItem("globalstudyid", study.studyid);
      sessionStorage.setItem("globalstudyname", study.studyname);
    } else {
      sessionStorage.removeItem("globalstudyid");
      sessionStorage.removeItem("globalstudyname");
    }

    // Hold the loading state for 800ms to allow route transitions and rendering to settle
    setTimeout(() => {
      setIsStudyLoading(false);
    }, 800);
  }, []);

  // Listen for logout signal from outside React (e.g. clearSessionStorage utility)
  useEffect(() => {
    const handler = () => setSelectedStudy(null);
    window.addEventListener("clearGlobalStudy", handler);
    return () => window.removeEventListener("clearGlobalStudy", handler);
  }, []);

  return (
    <StudyContext.Provider value={{ selectedStudy, setGlobalStudy, isStudyLoading, setIsStudyLoading }}>
      {children}
    </StudyContext.Provider>
  );
};

/* ---------- Hook ---------- */

export const useGlobalStudy = (): StudyContextType => {
  const context = useContext(StudyContext);
  if (context === undefined) {
    throw new Error("useGlobalStudy must be used within a <StudyProvider>");
  }
  return context;
};
